import { useState, type FormEvent } from 'react';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import { ApiError } from '../types/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { QrCode } from '../components/QrCode';

interface EnrollBegin {
  secretBase32: string;
  otpauthUri: string;
}

/**
 * Every role must have TOTP before the API serves tenant data (security
 * fix 2026-09-25 — the backend answers `403 totp_setup_required`). A user
 * without it lands here instead of the app: begin → scan the QR code (or type the secret) →
 * confirm with a live code → save the recovery codes → continue.
 */
export function TotpSetupPage() {
  const { refreshMe, logout } = useAuth();
  const [secret, setSecret] = useState<EnrollBegin | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function begin() {
    setError(null);
    setBusy(true);
    try {
      setSecret(
        await apiFetch<EnrollBegin>('/auth/totp/enroll/begin', { method: 'POST', body: {} }),
      );
    } catch {
      setError('Setup could not be started. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await apiFetch<{ recoveryCodes: string[] }>('/auth/totp/enroll/confirm', {
        method: 'POST',
        body: { code: code.replace(/\s/g, '') },
      });
      setRecoveryCodes(result.recoveryCodes);
    } catch (err) {
      if (isTotpLocked(err)) setError(TOTP_LOCKED_MESSAGE);
      else
        setError(err instanceof ApiError ? 'Incorrect code.' : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <Card className="w-[min(460px,100%)] p-8 flex flex-col gap-5">
        <div>
          <h1 className="text-lg font-semibold">Turn on two-factor authentication</h1>
          <p className="text-sm text-muted">
            All users must use an authenticator app before they can see data.
          </p>
        </div>

        {error ? <Alert>{error}</Alert> : null}

        {recoveryCodes ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm">
              Save these recovery codes somewhere safe. Each code can be used once if you lose your
              phone.
            </p>
            <ul aria-label="Recovery codes" className="font-mono text-sm grid grid-cols-2 gap-1">
              {recoveryCodes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button onClick={() => void refreshMe()}>I have saved the codes</Button>
          </div>
        ) : secret ? (
          <form className="flex flex-col gap-4" onSubmit={confirm}>
            <p className="text-sm">
              Scan the QR code with your authenticator app (Google Authenticator, 1Password,
              Microsoft Authenticator or similar).
            </p>
            <div className="self-center">
              <QrCode value={secret.otpauthUri} label="QR code for the authenticator app" />
            </div>
            <p className="text-sm text-muted">
              On this phone? Use <a href={secret.otpauthUri}>this link</a> or type the key:
            </p>
            <code aria-label="Secret key" className="font-mono text-sm break-all">
              {secret.secretBase32}
            </code>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="setup-totp">Code from the authenticator app</Label>
              <Input
                id="setup-totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123 456"
                required
                className="font-mono text-base tracking-widest"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy}>
              {busy ? 'Confirming…' : 'Confirm'}
            </Button>
          </form>
        ) : (
          <Button onClick={() => void begin()} disabled={busy}>
            {busy ? 'Starting…' : 'Start setup'}
          </Button>
        )}

        <Button variant="ghost" onClick={() => void logout()}>
          Log out
        </Button>
      </Card>
    </div>
  );
}
